'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

interface SystemMetrics {
  dispositivi_totali: number;
  dispositivi_online: number;
  dispositivi_offline: number;
  dispositivi_manutenzione: number;
  allarmi_critici: number;
  allarmi_alti: number;
  allarmi_medi: number;
  ticket_aperti: number;
  ticket_in_lavorazione: number;
  ip_totali: number;
  ip_assegnati: number;
  conflitti_ip: number;
  subnet_totali: number;
  vlan_totali: number;
  uptime_medio: number;
  sms_inviati_oggi: number;
  sms_falliti_oggi: number;
}

interface ServiceStatus {
  name: string;
  status: 'online' | 'degraded' | 'offline' | 'unknown';
  latency?: number;
  lastCheck: Date;
  icon: string;
}

interface HealthEvent {
  id: string;
  tipo: string;
  titolo: string;
  descrizione: string;
  severita: string;
  created_at: string;
}

const STATUS_CONFIG = {
  online: { label: 'Online', bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', dot: 'bg-green-400' },
  degraded: { label: 'Degradato', bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' },
  offline: { label: 'Offline', bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-400' },
  unknown: { label: 'Sconosciuto', bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/30', dot: 'bg-gray-400' },
};

const SEV_CONFIG: Record<string, { text: string; bg: string }> = {
  critico: { text: 'text-red-400', bg: 'bg-red-500/10' },
  alto: { text: 'text-orange-400', bg: 'bg-orange-500/10' },
  medio: { text: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  basso: { text: 'text-green-400', bg: 'bg-green-500/10' },
  info: { text: 'text-sky-400', bg: 'bg-sky-500/10' },
};

function HealthGauge({ value, max, label, color }: { value: number; max: number; label: string; color: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-color">{label}</span>
        <span className={`font-semibold ${color}`}>{pct}%</span>
      </div>
      <div className="h-2 bg-surface-elevated rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${color.replace('text-', 'bg-')}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-muted-color">{value} / {max}</div>
    </div>
  );
}

export default function SystemHealthContent() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [events, setEvents] = useState<HealthEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [
        { count: totale },
        { count: online },
        { count: offline },
        { count: manutenzione },
        { count: allarmiCritici },
        { count: allarmiAlti },
        { count: allarmiMedi },
        { count: ticketAperti },
        { count: ticketLavorazione },
        { count: ipTotali },
        { count: ipAssegnati },
        { count: conflitti },
        { count: subnet },
        { count: vlan },
        { count: smsInviati },
        { count: smsFalliti },
      ] = await Promise.all([
        supabase.from('devices').select('*', { count: 'exact', head: true }),
        supabase.from('devices').select('*', { count: 'exact', head: true }).eq('stato', 'online'),
        supabase.from('devices').select('*', { count: 'exact', head: true }).eq('stato', 'offline'),
        supabase.from('devices').select('*', { count: 'exact', head: true }).eq('stato', 'manutenzione'),
        supabase.from('allarmi').select('*', { count: 'exact', head: true }).eq('priorita', 'critico').in('stato', ['attivo', 'riconosciuto', 'in_gestione']),
        supabase.from('allarmi').select('*', { count: 'exact', head: true }).eq('priorita', 'alto').in('stato', ['attivo', 'riconosciuto', 'in_gestione']),
        supabase.from('allarmi').select('*', { count: 'exact', head: true }).eq('priorita', 'medio').in('stato', ['attivo', 'riconosciuto', 'in_gestione']),
        supabase.from('tickets').select('*', { count: 'exact', head: true }).eq('stato', 'aperto'),
        supabase.from('tickets').select('*', { count: 'exact', head: true }).eq('stato', 'in_lavorazione'),
        supabase.from('ip_addresses').select('*', { count: 'exact', head: true }),
        supabase.from('ip_addresses').select('*', { count: 'exact', head: true }).eq('stato', 'assegnato'),
        supabase.from('ip_addresses').select('*', { count: 'exact', head: true }).eq('stato', 'conflitto'),
        supabase.from('subnets').select('*', { count: 'exact', head: true }),
        supabase.from('vlans').select('*', { count: 'exact', head: true }),
        supabase.from('sms_queue').select('*', { count: 'exact', head: true }).eq('stato', 'inviato').gte('created_at', today.toISOString()),
        supabase.from('sms_queue').select('*', { count: 'exact', head: true }).eq('stato', 'fallito').gte('created_at', today.toISOString()),
      ]);

      setMetrics({
        dispositivi_totali: totale || 0,
        dispositivi_online: online || 0,
        dispositivi_offline: offline || 0,
        dispositivi_manutenzione: manutenzione || 0,
        allarmi_critici: allarmiCritici || 0,
        allarmi_alti: allarmiAlti || 0,
        allarmi_medi: allarmiMedi || 0,
        ticket_aperti: ticketAperti || 0,
        ticket_in_lavorazione: ticketLavorazione || 0,
        ip_totali: ipTotali || 0,
        ip_assegnati: ipAssegnati || 0,
        conflitti_ip: conflitti || 0,
        subnet_totali: subnet || 0,
        vlan_totali: vlan || 0,
        uptime_medio: 0,
        sms_inviati_oggi: smsInviati || 0,
        sms_falliti_oggi: smsFalliti || 0,
      });
      setLastUpdated(new Date());
    } catch {
      // silent
    }
  }, []);

  const checkServices = useCallback(async () => {
    const start = Date.now();
    const checks: ServiceStatus[] = [];

    // Check Supabase DB connectivity
    try {
      const t0 = Date.now();
      await supabase.from('devices').select('id', { count: 'exact', head: true });
      checks.push({ name: 'Database Supabase', status: 'online', latency: Date.now() - t0, lastCheck: new Date(), icon: 'CircleStackIcon' });
    } catch {
      checks.push({ name: 'Database Supabase', status: 'offline', lastCheck: new Date(), icon: 'CircleStackIcon' });
    }

    // Check alarms table
    try {
      const t0 = Date.now();
      await supabase.from('allarmi').select('id', { count: 'exact', head: true });
      checks.push({ name: 'Motore Allarmi', status: 'online', latency: Date.now() - t0, lastCheck: new Date(), icon: 'BellAlertIcon' });
    } catch {
      checks.push({ name: 'Motore Allarmi', status: 'offline', lastCheck: new Date(), icon: 'BellAlertIcon' });
    }

    // Check SMS queue
    try {
      const t0 = Date.now();
      await supabase.from('sms_queue').select('id', { count: 'exact', head: true });
      checks.push({ name: 'Coda SMS (Twilio)', status: 'online', latency: Date.now() - t0, lastCheck: new Date(), icon: 'DevicePhoneMobileIcon' });
    } catch {
      checks.push({ name: 'Coda SMS (Twilio)', status: 'offline', lastCheck: new Date(), icon: 'DevicePhoneMobileIcon' });
    }

    // Check activity log
    try {
      const t0 = Date.now();
      await supabase.from('activity_log').select('id', { count: 'exact', head: true });
      checks.push({ name: 'Log Attività', status: 'online', latency: Date.now() - t0, lastCheck: new Date(), icon: 'ClipboardDocumentListIcon' });
    } catch {
      checks.push({ name: 'Log Attività', status: 'offline', lastCheck: new Date(), icon: 'ClipboardDocumentListIcon' });
    }

    // Check network discovery
    try {
      const t0 = Date.now();
      await supabase.from('subnets').select('id', { count: 'exact', head: true });
      checks.push({ name: 'Monitoraggio Rete', status: Date.now() - start < 3000 ? 'online' : 'degraded', latency: Date.now() - t0, lastCheck: new Date(), icon: 'SignalIcon' });
    } catch {
      checks.push({ name: 'Monitoraggio Rete', status: 'offline', lastCheck: new Date(), icon: 'SignalIcon' });
    }

    setServices(checks);
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('storico_eventi')
        .select('id, tipo, titolo, descrizione, severita, created_at')
        .order('created_at', { ascending: false })
        .limit(10);
      setEvents((data || []) as HealthEvent[]);
    } catch {
      // silent
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchMetrics(), checkServices(), fetchEvents()]);
    setLoading(false);
  }, [fetchMetrics, checkServices, fetchEvents]);

  useEffect(() => {
    loadAll();

    const channel = supabase
      .channel('system_health_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'devices' }, fetchMetrics)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'allarmi' }, fetchMetrics)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'storico_eventi' }, fetchEvents)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [loadAll, fetchMetrics, fetchEvents]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchMetrics();
      checkServices();
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchMetrics, checkServices]);

  const overallStatus = (): 'online' | 'degraded' | 'offline' => {
    if (!services.length) return 'unknown' as any;
    const offline = services.filter(s => s.status === 'offline').length;
    const degraded = services.filter(s => s.status === 'degraded').length;
    if (offline > 0) return 'offline';
    if (degraded > 0) return 'degraded';
    return 'online';
  };

  const overall = overallStatus();
  const overallCfg = STATUS_CONFIG[overall] || STATUS_CONFIG.unknown;

  const uptimePct = metrics && metrics.dispositivi_totali > 0
    ? Math.round((metrics.dispositivi_online / metrics.dispositivi_totali) * 100)
    : 0;

  const ipUtilPct = metrics && metrics.ip_totali > 0
    ? Math.round((metrics.ip_assegnati / metrics.ip_totali) * 100)
    : 0;

  const timeAgo = (d: string) => {
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Adesso';
    if (mins < 60) return `${mins} min fa`;
    return `${Math.floor(mins / 60)} ore fa`;
  };

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb items={[{ label: 'System Health Monitor' }]} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Icon name="HeartIcon" size={28} className="text-sky-400" />
            System Health Monitor
          </h1>
          <p className="text-muted-color text-sm mt-1">
            Stato in tempo reale di tutti i servizi e componenti dell&apos;infrastruttura
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${autoRefresh ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-surface text-muted-color border-border'}`}
          >
            <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
          <button
            onClick={loadAll}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold disabled:opacity-50"
          >
            <Icon name="ArrowPathIcon" size={16} className={loading ? 'animate-spin' : ''} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className={`flex items-center gap-4 p-4 rounded-xl border ${overallCfg.bg} ${overallCfg.border}`}>
        <div className={`w-4 h-4 rounded-full ${overallCfg.dot} ${overall === 'online' ? 'animate-pulse' : ''}`} />
        <div className="flex-1">
          <div className={`text-lg font-bold ${overallCfg.text}`}>
            Sistema {overallCfg.label}
          </div>
          <div className="text-xs text-muted-color">
            {lastUpdated ? `Ultimo controllo: ${lastUpdated.toLocaleTimeString('it-IT')}` : 'Caricamento...'}
            {autoRefresh && ' · Aggiornamento automatico ogni 15s'}
          </div>
        </div>
        <div className={`text-3xl font-bold ${overallCfg.text}`}>{uptimePct}%</div>
        <div className="text-xs text-muted-color">Uptime dispositivi</div>
      </div>

      {/* Services Grid */}
      <div>
        <h2 className="text-sm font-bold text-muted-color uppercase tracking-wide mb-3">Servizi</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {services.map(svc => {
            const cfg = STATUS_CONFIG[svc.status];
            return (
              <div key={svc.name} className={`bg-surface border rounded-xl p-4 ${cfg.border}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                    <Icon name={svc.icon as any} size={18} className={cfg.text} />
                  </div>
                  <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${svc.status === 'online' ? 'animate-pulse' : ''}`} />
                </div>
                <div className="text-sm font-semibold text-white leading-tight">{svc.name}</div>
                <div className={`text-xs font-semibold mt-1 ${cfg.text}`}>{cfg.label}</div>
                {svc.latency !== undefined && (
                  <div className="text-xs text-muted-color mt-1">{svc.latency}ms</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Devices */}
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Icon name="ServerIcon" size={16} className="text-sky-400" />
            Dispositivi
          </h2>
          <HealthGauge
            value={metrics?.dispositivi_online || 0}
            max={metrics?.dispositivi_totali || 1}
            label="Online"
            color="text-green-400"
          />
          <HealthGauge
            value={metrics?.dispositivi_offline || 0}
            max={metrics?.dispositivi_totali || 1}
            label="Offline"
            color="text-red-400"
          />
          <HealthGauge
            value={metrics?.dispositivi_manutenzione || 0}
            max={metrics?.dispositivi_totali || 1}
            label="In Manutenzione"
            color="text-amber-400"
          />
          <div className="pt-2 border-t border-border grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-xl font-bold text-white">{metrics?.dispositivi_totali || 0}</div>
              <div className="text-xs text-muted-color">Totali</div>
            </div>
            <div>
              <div className="text-xl font-bold text-green-400">{uptimePct}%</div>
              <div className="text-xs text-muted-color">Uptime</div>
            </div>
          </div>
        </div>

        {/* Network */}
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Icon name="GlobeAltIcon" size={16} className="text-sky-400" />
            Rete e IP
          </h2>
          <HealthGauge
            value={metrics?.ip_assegnati || 0}
            max={metrics?.ip_totali || 1}
            label="IP Utilizzati"
            color={ipUtilPct > 85 ? 'text-red-400' : ipUtilPct > 70 ? 'text-amber-400' : 'text-green-400'}
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-surface-elevated rounded-lg p-3 text-center">
              <div className={`text-xl font-bold ${(metrics?.conflitti_ip || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {metrics?.conflitti_ip || 0}
              </div>
              <div className="text-xs text-muted-color">Conflitti IP</div>
            </div>
            <div className="bg-surface-elevated rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-sky-400">{metrics?.subnet_totali || 0}</div>
              <div className="text-xs text-muted-color">Subnet</div>
            </div>
            <div className="bg-surface-elevated rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-purple-400">{metrics?.vlan_totali || 0}</div>
              <div className="text-xs text-muted-color">VLAN</div>
            </div>
            <div className="bg-surface-elevated rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-white">{metrics?.ip_totali || 0}</div>
              <div className="text-xs text-muted-color">IP Totali</div>
            </div>
          </div>
        </div>

        {/* Alarms & Tickets */}
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Icon name="BellAlertIcon" size={16} className="text-sky-400" />
            Allarmi e Ticket
          </h2>
          <div className="space-y-2">
            {[
              { label: 'Critici', value: metrics?.allarmi_critici || 0, color: 'text-red-400', bg: 'bg-red-500/10' },
              { label: 'Alti', value: metrics?.allarmi_alti || 0, color: 'text-orange-400', bg: 'bg-orange-500/10' },
              { label: 'Medi', value: metrics?.allarmi_medi || 0, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`flex items-center justify-between px-3 py-2 rounded-lg ${bg}`}>
                <span className="text-sm text-secondary-color">{label}</span>
                <span className={`text-sm font-bold ${color}`}>{value}</span>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-border space-y-2">
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-sky-500/10">
              <span className="text-sm text-secondary-color">Ticket Aperti</span>
              <span className="text-sm font-bold text-sky-400">{metrics?.ticket_aperti || 0}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-500/10">
              <span className="text-sm text-secondary-color">In Lavorazione</span>
              <span className="text-sm font-bold text-blue-400">{metrics?.ticket_in_lavorazione || 0}</span>
            </div>
          </div>
          {/* SMS today */}
          <div className="pt-2 border-t border-border">
            <div className="text-xs text-muted-color mb-2">SMS Oggi (Twilio)</div>
            <div className="flex gap-3">
              <div className="flex-1 bg-green-500/10 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-green-400">{metrics?.sms_inviati_oggi || 0}</div>
                <div className="text-xs text-muted-color">Inviati</div>
              </div>
              <div className="flex-1 bg-red-500/10 rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-red-400">{metrics?.sms_falliti_oggi || 0}</div>
                <div className="text-xs text-muted-color">Falliti</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Events */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Icon name="ClockIcon" size={18} className="text-sky-400" />
            Ultimi Eventi di Sistema
          </h2>
          <span className="text-xs text-muted-color">{events.length} eventi recenti</span>
        </div>
        {events.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <Icon name="ClipboardDocumentListIcon" size={32} className="text-muted-color" />
            <p className="text-sm text-muted-color">Nessun evento recente</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {events.map(ev => {
              const sev = SEV_CONFIG[ev.severita] || SEV_CONFIG.info;
              return (
                <div key={ev.id} className="flex items-start gap-4 px-5 py-3.5 hover:bg-surface-elevated/30 transition-colors">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${sev.bg.replace('bg-', 'bg-').replace('/10', '-400')}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{ev.titolo}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${sev.bg} ${sev.text} font-semibold`}>{ev.severita}</span>
                    </div>
                    <p className="text-xs text-secondary-color mt-0.5 truncate">{ev.descrizione}</p>
                  </div>
                  <span className="text-xs text-muted-color flex-shrink-0 tabular-nums">{timeAgo(ev.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
